<template>
  <div class="accordion-panels">
    <div
      v-for="(accordionItem, accordiionKey) in accordionItems"
      :key="accordiionKey"
      class="accordion-panel"
    >
      <a
        v-if="accordionItem.anchor"
        :id="accordionItem.anchor"
        class="anchor"
      />
      <div
        class="panel-title"
        :class="{
          current:
            isCurrentCountry(accordionItem.class) || isActiveAnchor(accordionItem.anchor)
        }"
        data-type="accordion"
        @click="openPanel($event.target)"
      >
        <div
          class="panel-title-content"
          :class="{ 'with-icons': hasTitleIcon(accordionItem.titleIcon) }"
        >
          <div
            v-if="hasTitleIcon(accordionItem.titleIcon)"
            class="title-icon-wrap"
          >
            <img
              :src="getImageSource(accordionItem.titleIcon)"
              width="30"
              height="30"
              :alt="getImageAlt(accordionItem)"
            />
          </div>
          <span :class="accordionItem.class">{{ accordionItem.title }}</span>
        </div>
      </div>

      <div class="panel-content">
        <a
          v-if="isLinkSet(accordionItem)"
          :href="getLinkHref(accordionItem)"
          :target="getLinkTarget(accordionItem)"
          class="image-link"
        >
          <img
            v-if="backgroundUrl(accordionItem)"
            :alt="getAltText(accordionItem)"
            :src="backgroundUrl(accordionItem)"
          />
        </a>

        <img
          v-else-if="backgroundUrl(accordionItem)"
          :alt="getAltText(accordionItem)"
          :src="backgroundUrl(accordionItem)"
        />

        <div
          v-if="accordionItem.text"
          v-html="accordionItem.text"
          v-inlineModal
        />

        <div
          v-for="(item, index) in accordionItem.content"
          :key="index"
          class="panel-content-block"
        >
          <span>{{ item.title }}</span>
          <a :href="`tel:${item.phone}`">{{ item.phone }}</a>
          <span class="hint">{{ item.hint }}</span>
          <span
            v-dompurify-html="item.hours"
            class="working-hours"
          />
        </div>

        <div
          v-if="isBlueButtonSet(accordionItem)"
          class="btn btn-blue"
        >
          <a
            :href="getBlueButtonHref(accordionItem)"
            :target="getBlueButtonTarget(accordionItem)"
          >
            {{ getBlueButtonText(accordionItem) }}
          </a>
        </div>

        <div
          v-if="isPdfSet(accordionItem)"
          class="btn btn-pdf"
        >
          <a
            :href="getPdfHref(accordionItem)"
            :target="getPdfTarget(accordionItem)"
          >
            {{ getPdfText(accordionItem) }}
            <span>PDF {{ accordionItem.pdfFileSize }}</span>
          </a>
        </div>

        <inner-content
          v-if="accordionItem.innerContent"
          :data="accordionItem.innerContent"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import has from 'lodash-es/has'
  import isEmpty from 'lodash-es/isEmpty'
  import { mapState, mapGetters } from 'vuex'
  import InnerContent from 'components/Accordion/InnerContent'
  import InlineModal from 'utils/directives/InlineModal'

  export default {
    components: {
      InnerContent
    },
    directives: {
      InlineModal
    },
    props: {
      accordionItems: {
        type: Array,
        required: true
      }
    },
    computed: {
      international() {
        let isInternational = true
        this.accordionItems.forEach((item) => {
          if (
            item.class &&
            item.class.replace('flag-', '') === this.selectedCountry.toLowerCase()
          ) {
            isInternational = false
          }
        })
        return isInternational
      },
      ...mapGetters('device', ['isMobile']),
      ...mapState('main', ['selectedCountry'])
    },
    methods: {
      isActiveAnchor(anchor) {
        return '#' + anchor === window.location.hash
      },
      openPanel(target) {
        target.parentNode.classList.toggle('current')
      },
      isBlueButtonSet(accordionItem) {
        return has(accordionItem, 'blueButton')
          ? !isEmpty(accordionItem.blueButton.href) &&
              !isEmpty(accordionItem.blueButton.text)
          : accordionItem.blueButtonLink &&
              !isEmpty(accordionItem.blueButtonLink) &&
              !isEmpty(accordionItem.blueButtonText)
      },
      isPdfSet(accordionItem) {
        return has(accordionItem, 'pdfButton')
          ? !isEmpty(accordionItem.pdfButton.href) &&
              !isEmpty(accordionItem.pdfButton.text)
          : accordionItem.pdfButtonLink &&
              !isEmpty(accordionItem.pdfButtonLink) &&
              !isEmpty(accordionItem.pdfButtonText)
      },
      getBlueButtonHref(accordionItem) {
        return has(accordionItem, 'blueButton')
          ? accordionItem.blueButton.href
          : accordionItem.blueButtonLink
      },
      getPdfHref(accordionItem) {
        return has(accordionItem, 'pdfButton')
          ? accordionItem.pdfButton.href
          : accordionItem.pdfButtonLink
      },
      getPdfTarget(accordionItem) {
        return has(accordionItem, 'pdfButton') ? accordionItem.pdfButton.target : '_blank'
      },
      getPdfText(accordionItem) {
        return has(accordionItem, 'pdfButton')
          ? accordionItem.pdfButton.text
          : accordionItem.pdfButtonText
      },
      getBlueButtonText(accordionItem) {
        return has(accordionItem, 'blueButton')
          ? accordionItem.blueButton.text
          : accordionItem.blueButtonText
      },
      getBlueButtonTarget(accordionItem) {
        return has(accordionItem, 'blueButton')
          ? accordionItem.blueButton.target
          : '_blank'
      },
      hasTitleIcon(titleIcon) {
        return has(titleIcon, 'source') ? !isEmpty(titleIcon.source) : !isEmpty(titleIcon)
      },
      getImageSource(image) {
        return has(image, 'source') ? image.source : image
      },
      getImageAlt(accordionItem) {
        return has(accordionItem, 'titleIcon.alt')
          ? accordionItem.titleIcon.alt
          : accordionItem.title
      },
      isLinkSet(accordionItem) {
        return has(accordionItem, 'link.href')
          ? !isEmpty(accordionItem.link.href)
          : !isEmpty(accordionItem.link)
      },
      getLinkHref(link) {
        return has(link, 'href') ? link.href : link
      },
      getLinkTarget(content) {
        return has(content, 'link.target') ? content.link.target : content.linkTarget
      },
      backgroundUrl(accordionItem) {
        if (this.isMobile) {
          return has(accordionItem, 'imgMobile.source')
            ? accordionItem.imgMobile.source
            : accordionItem.imgMobile
        }

        return has(accordionItem, 'imgDesktop.source')
          ? accordionItem.imgDesktop.source
          : accordionItem.imgDesktop
      },
      getAltText(accordionItem) {
        if (this.isMobile) {
          return has(accordionItem, 'imgMobile.alt')
            ? accordionItem.imgMobile.alt
            : accordionItem.imgMobileAlt
        }

        return has(accordionItem, 'imgDesktop.alt')
          ? accordionItem.imgDesktop.alt
          : accordionItem.imgDesktopAlt
      },
      isSelectedCountry(flagClass) {
        return (
          flagClass &&
          flagClass.replace('flag-', '') === this.selectedCountry.toLowerCase()
        )
      },
      isCurrentCountry(flagClass) {
        return (
          flagClass &&
          (this.isSelectedCountry(flagClass) ||
            (this.international && flagClass === 'flag-zz'))
        )
      }
    }
  }
</script>

<style
  lang="scss"
  scoped
  rel="stylesheet/scss"
>
  @import '~assets/styles/common.scss';
  @import '~assets/styles/flags-sprite-img.scss';

  .anchor {
    display: block;
    position: relative;
    top: -66px;
    visibility: hidden;

    @include bp-mf-tablet {
      top: -148px;
    }
  }

  .accordion-panel {
    margin-bottom: 8px;
    counter-increment: panel-count-me;

    @include bp-tablet-only {
      margin-bottom: 12px;
    }

    @include bp-mobile {
      margin-bottom: 0;
    }

    @media print {
      border: 1px solid $grey-border;
    }
  }

  .panel-title {
    display: flex;
    flex-direction: column;

    &.current {
      + .panel-content {
        height: auto;
        padding: 0 32px 36px;

        @include bp-tablet-only {
          padding: 0 17px 33px;
        }

        @include bp-mobile {
          padding: 0 23px 33px;
        }
      }

      .panel-title-content.with-icons .title-icon-wrap {
        @include bp-mf-desktop {
          height: calc(100% + 4px);
        }
      }
    }
  }

  .panel-title-content {
    display: flex;
    align-items: center;
    position: relative;
    padding: 15px 80px 15px 78px;
    min-height: 75px;
    border: 1px solid $grey-border;
    border-radius: 6px;
    cursor: pointer;

    @include bp-tablet-only {
      padding: 15px 47px 15px 55px;
      min-height: 61px;
      border-radius: 4px;
    }

    @include bp-mobile {
      padding: 15px 59px 15px 78px;
      min-height: 60px;
      border: 0;
      border-top: 1px solid $grey-border;
      border-radius: 0;
    }

    &.with-icons {
      &:before {
        display: none;
      }

      .title-icon-wrap {
        display: flex;
      }
    }

    .title-icon-wrap {
      display: none;
      position: absolute;
      top: 0;
      left: 23px;
      width: auto;
      height: 100%;
      align-items: center;
      justify-content: center;
      pointer-events: none;

      @include bp-tablet-only {
        left: 17px;
      }

      @include bp-mf-desktop {
        left: 0;
        width: 78px;
      }

      img {
        opacity: 0.25;
        width: 25px;
        height: 25px;

        @include bp-tablet-only {
          width: 27px;
          height: 27px;
        }

        @include bp-mf-desktop {
          width: 30px;
          height: 30px;
        }
      }
    }

    &:before {
      @include nrListStyle();
    }

    &:after {
      content: $icon-arrow-right-thin;
      @include icon-font-thin();
      position: absolute;
      top: calc(50% - 7px);
      right: 35px;
      transform: rotate(90deg);
      text-align: center;
      font-weight: 700;
      line-height: 1;
      color: $blue-medium;

      @include bp-tablet-only {
        right: 20px;
      }

      @include bp-tablet {
        top: calc(50% - 5px);
        font-size: 12px;
      }

      @include bp-mobile {
        right: 23px;
      }
    }

    &:hover {
      @include bp-mf-desktop {
        background-color: $gray-lighter-background;
        border: 1px solid $gray-lighter-background;
      }
    }

    @media print {
      min-height: 72px;
      background-color: white;
      border: 1px solid $gray-lighter-background;
      border-radius: 6px 6px 0 0;

      &:before {
        top: calc(50% - 11px);
        @include nrListCurrentStyle();
      }

      &:after {
        top: calc(50% - 6px);
        transform: rotate(270deg);
      }

      span {
        position: relative;
        top: 2px;
      }
    }

    .panel-title.current & {
      min-height: 72px;
      background-color: $gray-lighter-background;
      border: 1px solid $gray-lighter-background;
      border-radius: 6px 6px 0 0;

      @include bp-tablet {
        min-height: 61px;
      }

      @include bp-mobile {
        min-height: 60px;
        border: none;
        border-top: 1px solid $grey-border;
      }

      &:before {
        @include nrListCurrentStyle();

        @include bp-mf-desktop {
          top: calc(50% - 16px);
        }
      }

      &:after {
        top: calc(50% - 6px);
        transform: rotate(270deg);
      }

      span {
        @include bp-mf-desktop {
          position: relative;
          top: 2px;
        }
      }
    }

    span {
      pointer-events: none;
      @include h4Style();
      margin-bottom: 0 !important;
      width: 100%;
    }
  }

  .panel-content {
    overflow: hidden;
    padding: 0 32px;
    height: 0;
    background-color: $gray-lighter-background;
    border-radius: 0 0 6px 6px;
    transition: all 0.3s ease;

    @include bp-tablet-only {
      padding: 0 17px;
    }

    @include bp-mobile {
      padding: 0 23px;
    }

    @media print {
      height: auto;
      padding: 0 32px 16px;
      background-color: white;
    }

    .image-link {
      display: block;
    }

    ::v-deep img {
      margin-bottom: 28px;
      width: 100%;

      @include bp-mobile {
        margin-bottom: 21px;
      }
    }

    ::v-deep h3 {
      @include h3Style();
    }

    > div ::v-deep h4:first-child {
      padding-top: 0;
      margin-top: 0;
      border-top: none;
    }

    ::v-deep h4 {
      margin: 31px 0 13px;
      padding-top: 30px;
      border-top: 1px solid $gray-medium-border;
      @include h4Style();

      @include bp-tablet {
        margin: 20px 0 7px;
        padding-top: 18px;
      }
    }

    ::v-deep p {
      @include body16Style();

      &:not(:last-child) {
        margin-bottom: 10px;
      }

      a {
        color: $blue-medium;

        &:hover {
          color: $blue-light;
        }

        &.btn-text-white {
          color: white;

          &:hover {
            color: white;
          }
        }
      }
    }

    ::v-deep ul li > ul li {
      font-size: 1em;
      margin: 0;
    }

    ::v-deep strong {
      font-weight: 500;
    }

    .btn-blue {
      margin: 32px 0 20px;
      min-width: 180px;
      height: 46px;

      @include bp-tablet {
        margin-bottom: 21px;
      }

      @include bp-mobile {
        margin-top: 28px;
      }

      a {
        padding: 0 20px;
        line-height: 46px;

        @include bp-mobile {
          font-size: em(13);
        }
      }
    }

    .btn-pdf {
      position: relative;
      margin-top: 15px;
      height: auto;
      max-width: inherit;
      border-radius: 4px;
      border: 1px solid $grey-border;

      @include bp-mf-tablet {
        margin-top: 25px;
      }

      &:hover {
        border-color: $blue-medium;
      }

      &:before {
        content: $line-icon-clipboard-lines;
        @include line-icon-font();
        position: absolute;
        top: 12px;
        left: 20px;
        font-size: em(22);
        color: $blue-medium;
        pointer-events: none;

        @include bp-mf-tablet {
          top: calc(50% - 12px);
        }
      }

      a {
        display: block;
        padding: 7px 65px 7px 55px;
        font-weight: 500;
        line-height: 20px;
        text-decoration: none;
        color: $blue-medium;

        @include bp-mf-tablet {
          padding: 12px 20px 12px 55px;
        }

        span {
          display: block;
          font-weight: 400;
          color: $grey;
        }
      }
    }
  }
</style>
